// Kontakt.js – Seite /kontakt: Kontaktformular (EmailJS) mit Prüfung, Honeypot-Spamschutz und Erfolgs-/Fehlermeldung.
// Nach erfolgreichem Senden wird die Conversion über consent.js gemeldet (nur nach Einwilligung).
import "./Home.css";
import "./custom.css";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import emailjs from "@emailjs/browser";
import { useEffect, useRef, useState } from "react";
import { meldeKontaktanfrage } from "../../consent";
import usePageMeta from "../../usePageMeta";
import betrieb, { telefonLink, mailLink } from "../../data/betrieb";
import { JsonLd, webseiteSchema, BETRIEB_ID } from "../Strukturdaten";

// Seitentitel einmal definiert, damit <title> und Strukturdaten nicht auseinanderlaufen
const TITEL = `Kontakt: Baumpflege & Landschaftspflege anfragen | ${betrieb.name}`;

// Strukturdaten: diese Seite trägt die Kontaktdaten des Betriebs
const schema = webseiteSchema({
  typ: "ContactPage",
  pfad: "/kontakt",
  name: TITEL,
  about: { "@id": BETRIEB_ID },
});

// Die name-Attribute (name, email, phone, message) verwendet die EmailJS-Vorlage – nicht umbenennen!
const FELDER = [
  { id: "name",  label: "Name",                     type: "text",  placeholder: "Ihr Name",            autoComplete: "name",  pflicht: true },
  { id: "email", label: "E-Mail",                   type: "email", placeholder: "Ihre E-Mail-Adresse", autoComplete: "email", pflicht: true },
  { id: "phone", label: "Telefonnummer (optional)", type: "tel",   placeholder: "Für einen Rückruf",    autoComplete: "tel",   pflicht: false },
];

// Spam-Schutz (Honeypot): Dieses Feld ist für Menschen unsichtbar. Programme, die Formulare automatisch ausfüllen,
// tragen dort meist etwas ein – dann wird nichts gesendet. Bewusst kein Name wie „firma“ oder „company“,
// sonst füllt die Autovervollständigung des Browsers das Feld bei echten Besuchern aus.
const HONIGTOPF = "homepage";

const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TELEFON_MUSTER = /^[0-9+()/\s-]{5,}$/;

function pruefe(id, wert) {
  const v = (wert || "").toString().trim();
  switch (id) {
    case "name":
      return v ? "" : "Bitte geben Sie Ihren Namen ein.";
    case "email":
      if (!v) return "Bitte geben Sie Ihre E-Mail-Adresse ein.";
      return EMAIL_MUSTER.test(v) ? "" : "Bitte prüfen Sie die E-Mail-Adresse (Beispiel: name@beispiel.de).";
    case "phone":
      return !v || TELEFON_MUSTER.test(v) ? "" : "Bitte prüfen Sie die Telefonnummer (nur Ziffern, Leerzeichen und + / - ( )).";
    case "message":
      return v ? "" : "Bitte schreiben Sie kurz, worum es geht.";
    default:
      return "";
  }
}

const fehlerStil = { color: "var(--ff-grass)", fontSize: ".9rem", marginTop: ".4rem", marginBottom: 0 };
const linkStil = { color: "var(--ff-grass)" };

function Kontakt() {
  usePageMeta({
    path: "/kontakt",
    title: TITEL,
    description:
      "Kontakt zu Forst & Flur in Probsteierhagen: Baumpflege, Baumfällung, Mulchen oder Rückearbeiten in Schleswig-Holstein telefonisch oder per Formular anfragen.",
  });

  const form = useRef();
  const erfolgUeberschrift = useRef();
  const [emailSent, setEmailSent] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [sendefehler, setSendefehler] = useState(false);
  const [fehler, setFehler] = useState({});

  // Nach dem Senden den Fokus auf die Erfolgsmeldung setzen (Screenreader lesen sie vor, Tastatur startet dort)
  useEffect(() => {
    if (emailSent && erfolgUeberschrift.current) erfolgUeberschrift.current.focus();
  }, [emailSent]);

  // Fehlermeldung eines Feldes beim Korrigieren sofort aktualisieren
  const beiAenderung = (e) => {
    const { name, value } = e.target;
    if (fehler[name]) setFehler((alt) => ({ ...alt, [name]: pruefe(name, value) }));
  };

  const sendEmail = (e) => {
    e.preventDefault();
    if (sendet) return;
    const daten = new FormData(e.target);

    // Honeypot ausgefüllt: so tun, als wäre alles gut, aber nichts senden und keine Conversion melden
    if ((daten.get(HONIGTOPF) || "").toString().trim()) {
      setEmailSent(true);
      return;
    }

    const neueFehler = {};
    ["name", "email", "phone", "message"].forEach((id) => {
      const meldung = pruefe(id, daten.get(id));
      if (meldung) neueFehler[id] = meldung;
    });
    setFehler(neueFehler);
    const erstesFehlerfeld = Object.keys(neueFehler)[0];
    if (erstesFehlerfeld) {
      document.getElementById(erstesFehlerfeld)?.focus();
      return;
    }

    setSendet(true);
    setSendefehler(false);
    emailjs
      .sendForm(
        "service_obqnump",
        "template_aa1id6k",
        e.target,
        "2WUzq7MrPTDB3cvUE"
      )
      .then(
        () => {
          setSendet(false);
          setEmailSent(true);
          meldeKontaktanfrage(); // nur mit Einwilligung (src/consent.js)
        },
        () => {
          setSendet(false);
          setSendefehler(true);
        }
      );
  };

  const fehlerProps = (id) =>
    fehler[id] ? { "aria-invalid": true, "aria-describedby": `${id}-fehler` } : {};
  const fehlerText = (id) =>
    fehler[id] ? <p id={`${id}-fehler`} style={fehlerStil}>{fehler[id]}</p> : null;

  if (emailSent) {
    return (
      <div className="hero-container2">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <div className="ff-form-panel text-center">
                <div aria-hidden="true" style={{ fontSize: "3.5rem", color: "var(--ff-grass)", marginBottom: "1rem", fontFamily: "var(--fh)", fontWeight: 900 }}>
                  ✓
                </div>
                <h1
                  ref={erfolgUeberschrift}
                  tabIndex={-1}
                  style={{ fontFamily: "var(--fh)", fontWeight: 900, fontSize: "2.2rem", textTransform: "uppercase", color: "var(--ff-white)", marginBottom: "1rem", lineHeight: 1 }}
                >
                  Nachricht gesendet
                </h1>
                <p style={{ color: "var(--ff-muted)", marginBottom: "2rem" }}>
                  Vielen Dank! Wir werden uns so schnell wie möglich bei Ihnen melden.
                </p>
                <Link to="/" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                  Zurück zur Startseite
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="hero-container2">
      <JsonLd daten={schema} />
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <div className="ff-form-panel">
              <span className="ff-eyebrow" style={{ textAlign: "center", display: "block" }}>
                {betrieb.name}
              </span>
              <h1
                style={{
                  fontFamily: "var(--fh)",
                  fontWeight: 900,
                  fontSize: "clamp(2rem, 6vw, 2.8rem)",
                  textTransform: "uppercase",
                  color: "var(--ff-white)",
                  textAlign: "center",
                  marginBottom: "1rem",
                  lineHeight: 1,
                }}
              >
                Kontakt aufnehmen
              </h1>
              <p style={{ color: "var(--ff-muted)", textAlign: "center", marginBottom: "1rem", fontSize: ".95rem" }}>
                Rufen Sie uns an oder füllen Sie das Formular aus.
                Wir melden uns so schnell wie möglich bei Ihnen.
              </p>
              {/* Name (Vorzeile), Telefon, Erreichbarkeit, E-Mail und Anschrift aus src/data/betrieb.js */}
              <p style={{ color: "var(--ff-muted)", textAlign: "center", marginBottom: "2rem", fontSize: ".95rem" }}>
                Telefon: <a href={telefonLink} style={linkStil}>{betrieb.telefon}</a>
                <br />
                Telefonisch erreichbar: {betrieb.erreichbarkeit.text}
                <br />
                E-Mail: <a href={mailLink} style={linkStil}>{betrieb.email}</a>
                <br />
                Anschrift: {betrieb.strasse}, {betrieb.plz} {betrieb.ort}
              </p>

              {/* noValidate: statt der Browser-Hinweise eigene, deutsche Meldungen direkt am Feld */}
              <form onSubmit={sendEmail} ref={form} noValidate style={{ position: "relative" }}>
                {FELDER.map(({ id, label, type, placeholder, autoComplete, pflicht }) => (
                  <div className="mb-3" key={id}>
                    <label htmlFor={id} className="ff-form-label">{label}</label>
                    <input
                      type={type}
                      className="ff-form-input"
                      id={id}
                      name={id}
                      placeholder={placeholder}
                      autoComplete={autoComplete}
                      required={pflicht}
                      onChange={beiAenderung}
                      {...fehlerProps(id)}
                    />
                    {fehlerText(id)}
                  </div>
                ))}

                <div className="mb-4">
                  <label htmlFor="message" className="ff-form-label">Nachricht</label>
                  <textarea
                    className="ff-form-input"
                    id="message"
                    name="message"
                    rows="4"
                    style={{ resize: "vertical" }}
                    placeholder="Worum geht es? Baum oder Fläche, Ort und wann es Ihnen passt."
                    required
                    onChange={beiAenderung}
                    {...fehlerProps("message")}
                  />
                  {fehlerText("message")}
                </div>

                {/* Honeypot (siehe oben): außerhalb des sichtbaren Bereichs, nicht per Tab erreichbar */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label htmlFor={HONIGTOPF}>Bitte dieses Feld leer lassen</label>
                  <input type="text" id={HONIGTOPF} name={HONIGTOPF} tabIndex={-1} autoComplete="off" />
                </div>

                {sendefehler && (
                  <div role="alert" className="mb-3" style={{ color: "var(--ff-white)", fontSize: ".95rem" }}>
                    Ihre Nachricht konnte leider nicht gesendet werden. Bitte versuchen Sie es später noch einmal
                    oder melden Sie sich direkt: Telefon{" "}
                    <a href={telefonLink} style={linkStil}>{betrieb.telefon}</a>, E-Mail{" "}
                    <a href={mailLink} style={linkStil}>{betrieb.email}</a>.
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-ff-primary w-100"
                  disabled={sendet}
                  style={sendet ? { opacity: 0.75, cursor: "wait" } : undefined}
                >
                  {sendet ? "Wird gesendet …" : "Nachricht senden"}
                </button>
                <p style={{ color: "var(--ff-muted)", fontSize: ".85rem", marginTop: "1rem", marginBottom: 0 }}>
                  Ihre Angaben werden über den Dienst EmailJS an uns übermittelt und nur zur Bearbeitung Ihrer Anfrage
                  verwendet. Details in der{" "}
                  <Link to="/datenschutz" style={{ color: "var(--ff-grass)" }} onClick={() => window.scrollTo(0, 0)}>
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
              </form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Kontakt;
